/**
 * Enums de vehículo
 * Enumeraciones relacionadas con vehículos
 */

export enum VehicleSize {
    SMALL = 'SMALL',
    MEDIUM = 'MEDIUM',
    LARGE = 'LARGE'
}

export const vehicleSizeDescriptions: Record<VehicleSize, string> = {
    [VehicleSize.SMALL]: 'vehicleSize.SMALL',
    [VehicleSize.MEDIUM]: 'vehicleSize.MEDIUM',
    [VehicleSize.LARGE]: 'vehicleSize.LARGE'
};

export enum FuelType {
    GASOLINE = 'GASOLINE',
    DIESEL = 'DIESEL',
    ELECTRIC = 'ELECTRIC',
    HYBRID = 'HYBRID',
    PLUG_IN_HYBRID = 'PLUG_IN_HYBRID',
    LPG = 'LPG',
    CNG = 'CNG',
    COMBUSTION = 'COMBUSTION'
}

export const FuelTypeMapping: { [key: string]: FuelType } = {
    // Casos principales: la clave es el valor que recibes del proxy (en mayúsculas)
    // El valor es la constante de tu enum (ej: FuelType.GASOLINE)
    
    // Mapeo de la gasolina
    'PETROL': FuelType.GASOLINE,
    'GASOLINE': FuelType.GASOLINE, // Incluimos 'GASOLINE' por si el proxy lo usa a veces

    // Otros tipos de combustible
    'DIESEL': FuelType.DIESEL,
    'ELECTRIC': FuelType.ELECTRIC,
    'HYBRID': FuelType.HYBRID,
    'PLUG-IN HYBRID': FuelType.PLUG_IN_HYBRID, // ¡Atención al guion aquí!
    'LPG': FuelType.LPG,
    'CNG': FuelType.CNG,
    
    // Si la API devuelve un valor más corto para Plug-in Hybrid
    'PHEV': FuelType.PLUG_IN_HYBRID, 
    // Si la API devuelve el término 'FLEXIBLE' o 'ETHANOL'
    'ETHANOL': FuelType.GASOLINE, // O puedes mapearlo a un valor específico si lo añades al enum
    'COMBUSTION': FuelType.COMBUSTION,
};
export const fuelTypeDescriptions: Record<FuelType, string> = {
    [FuelType.GASOLINE]: 'fuelType.GASOLINE',
    [FuelType.DIESEL]: 'fuelType.DIESEL',
    [FuelType.ELECTRIC]: 'fuelType.ELECTRIC',
    [FuelType.HYBRID]: 'fuelType.HYBRID',
    [FuelType.PLUG_IN_HYBRID]: 'fuelType.PLUG_IN_HYBRID',
    [FuelType.LPG]: 'fuelType.LPG',
    [FuelType.CNG]: 'fuelType.CNG',
    [FuelType.COMBUSTION]: 'fuelType.COMBUSTION'
};

export enum BodyType {
    SEDAN = 'SEDAN',
    COUPE = 'COUPE',
    HATCHBACK = 'HATCHBACK',
    SUV = 'SUV',
    WAGON = 'WAGON',
    VAN = 'VAN',
    PICKUP = 'PICKUP',
    CONVERTIBLE = 'CONVERTIBLE',
    MINIVAN = 'MINIVAN',
    ESTATE = 'ESTATE',
    CHASSIS_CAB_VAN = 'CHASSIS_CAB_VAN',
    TRUCK = 'TRUCK'
}

export const bodyTypeDescriptions: Record<BodyType, string> = {
    [BodyType.SEDAN]: 'bodyType.SEDAN',
    [BodyType.COUPE]: 'bodyType.COUPE',
    [BodyType.HATCHBACK]: 'bodyType.HATCHBACK',
    [BodyType.SUV]: 'bodyType.SUV',
    [BodyType.WAGON]: 'bodyType.WAGON',
    [BodyType.VAN]: 'bodyType.VAN',
    [BodyType.PICKUP]: 'bodyType.PICKUP',
    [BodyType.CONVERTIBLE]: 'bodyType.CONVERTIBLE',
    [BodyType.MINIVAN]: 'bodyType.MINIVAN',
    [BodyType.ESTATE]: 'bodyType.ESTATE',
    [BodyType.CHASSIS_CAB_VAN]: 'bodyType.CHASSIS_CAB_VAN',
    [BodyType.TRUCK]: 'bodyType.TRUCK'
};

export const BodyTypeData: Record<BodyType, { label: string; image: string }> = {
    [BodyType.HATCHBACK]: {
        label: "Hatchback",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/hatchback.png"
    },
    [BodyType.SEDAN]: {
        label: "Sedan",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/sedan.png"
    },
    [BodyType.SUV]: {
        label: "SUV",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/SUV.png"
    },
    [BodyType.ESTATE]: {
        label: "Estate",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/estate.png"
    },
    [BodyType.COUPE]: {
        label: "Coupe",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/coupe-descapotable.png"
    },
    [BodyType.PICKUP]: {
        label: "Pickup",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/furgoneta-van-3.png"
    },
    [BodyType.MINIVAN]: {
        label: "Minivan",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/furgoneta-van-4.png"
    },
    [BodyType.CHASSIS_CAB_VAN]: {
        label: "Furgoneta",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/furgoneta-van-2.png"
    },
    [BodyType.TRUCK]: {
        label: "Furgoneta grande",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/furgoneta-van-1.png"
    },
    [BodyType.WAGON]: {
        label: "Wagon",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/estate.png"
    },
    [BodyType.VAN]: {
        label: "Van",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/furgoneta-van-2.png"
    },
    [BodyType.CONVERTIBLE]: {
        label: "Convertible",
        image: "https://roadit-dev.s3.eu-west-1.amazonaws.com/vehicles/coupe-descapotable.png"
    }
};

export enum VehicleCardState {
    SEARCH = 'SEARCH',       // Estado inicial: solo input de matrícula para buscar/añadir
    VIEW = 'VIEW',           // Visualización normal: muestra datos con botones de editar/borrar
    EDIT = 'EDIT',           // Edición: formulario para modificar un vehículo existente
    CREATE = 'CREATE',       // Creación: formulario abierto para un vehículo nuevo (vacío)
    READ_ONLY = 'READ_ONLY'  // Visualización restringida: sin acciones (para resúmenes o histórico)
}
